import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Group, Tabs, Text} from '@mantine/core';
import React, {useMemo} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ReviewSummary} from 'constants/SourceConstants';
import {SkippableBenchlingQueryResult} from 'graphql-client/BenchlingQuery';
import usePreference from 'ui-library/hooks/usePreference';
import strUtil from 'util/strUtil';

import {HomepageV2AssignedProcessTasksQuery} from '../__generated/HomepageV2AssignedProcessTasksQuery';
import CardSection from '../CardSection';
import {HOMEPAGE_SECTION_TEST_COMPONENT, HomepagePreferences} from '../HomepageConstants';
import EntriesRows from './EntriesRows';
import {MyWorkCategory, WORKSHEET_REVIEW_ITEM_STATUS_LABELS} from './MyWorkConstants';
import {makeTasksReviewSummary} from './MyWorkUtils';
import ReviewableMyWorkContent from './ReviewableMyWorkContent';
import TasksMyWorkContent from './TasksMyWorkContent';
import TemplatesRows from './TemplatesRows';
import WorksheetsRows from './WorksheetsRows';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

/** Left-to-right tab order in the My Work category tabs. */
const MY_WORK_CATEGORY_PRECEDENCE = [
  MyWorkCategory.ENTRIES,
  MyWorkCategory.WORKSHEETS,
  MyWorkCategory.TEMPLATES,
  MyWorkCategory.TASKS,
];

function getLeftmostEnabledCategory(categories: Array<MyWorkCategory>): MyWorkCategory {
  return MY_WORK_CATEGORY_PRECEDENCE.find((category) => categories.includes(category))!;
}

const useMyWorkContentStyles = createStyles((theme) => ({
  filterRow: {
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    padding: `${theme.spacing.sm} ${theme.spacing.md} 0`,
  },
  categoryTabsList: {
    borderBottom: 'none',
  },
  categoryTab: {
    // Mantine default is -2; nudge the underline up 1px to sit on the filter-row border.
    marginBottom: -1,
    '&[data-active]': {
      color: theme.colors.blue[8],
    },
  },
}));

interface MyWorkSectionContentProps {
  entryReviewSummary: ReviewSummary | null;
  worksheetReviewSummary: ReviewSummary | null;
  templateReviewSummary: ReviewSummary | null;
  tasksQueryResult: SkippableBenchlingQueryResult<HomepageV2AssignedProcessTasksQuery>;
  enabledCategories: Array<MyWorkCategory>;
}

/**
 * Renders a table displaying the current user's entries, worksheets, templates, or tasks and their status.
 * Also renders a status filter and a toggle to select which of the three entity types is being viewed.
 */
export default function MyWorkSectionContent({
  entryReviewSummary,
  worksheetReviewSummary,
  templateReviewSummary,
  tasksQueryResult,
  enabledCategories,
}: MyWorkSectionContentProps): JSX.Element {
  const [preferredCategory, setPreferredCategory] = usePreference<MyWorkCategory>(
    HomepagePreferences.MY_WORK_SELECTED_CATEGORY_V2
  );
  const selectedCategory = enabledCategories.includes(preferredCategory)
    ? preferredCategory
    : getLeftmostEnabledCategory(enabledCategories);
  const {classes} = useMyWorkContentStyles();

  const tasksReviewSummary = useMemo(() => makeTasksReviewSummary(tasksQueryResult), [tasksQueryResult]);

  return (
    <CardSection title="My work" dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}>
      <Group spacing="xs" className={classes.filterRow}>
        {/* If there's only one category, don't show the category tabs */}
        {enabledCategories.length === 1 ? (
          <Text size="lg" ml="sm">
            {strUtil.toSentenceCase(enabledCategories[0].toString())}
          </Text>
        ) : (
          <Tabs
            value={selectedCategory}
            onTabChange={(category) => {
              if (category != null) {
                setPreferredCategory(category as MyWorkCategory);
              }
            }}
          >
            <Tabs.List
              aria-label="Select category"
              className={classes.categoryTabsList}
              {...getTestProps({component: TEST_COMPONENT, element: 'categoryControl'})}
            >
              {enabledCategories.map((category) => (
                <Tabs.Tab key={category} value={category} className={classes.categoryTab}>
                  <Text size="lg">{strUtil.toSentenceCase(category.toString())}</Text>
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        )}
      </Group>
      {selectedCategory === MyWorkCategory.ENTRIES && (
        <ReviewableMyWorkContent
          ariaLabel="Select entry status"
          entityTypeLabel="entries"
          modelTypes={[ModelTypeConstants.ENTRY, ModelTypeConstants.STAGE_ENTRY]}
          preferenceKey={HomepagePreferences.MY_WORK_ENTRIES_STATUS_FILTER_V2}
          reviewSummary={entryReviewSummary}
          renderRows={({footerAction, hasCategoryWork, selectedIds}) => (
            <EntriesRows
              selectedEntryIds={selectedIds}
              hasCategoryWork={hasCategoryWork}
              footerAction={footerAction}
            />
          )}
        />
      )}
      {selectedCategory === MyWorkCategory.WORKSHEETS && (
        <ReviewableMyWorkContent
          ariaLabel="Select worksheet status"
          entityTypeLabel="worksheets"
          modelTypes={[ModelTypeConstants.WORKSHEET]}
          preferenceKey={HomepagePreferences.MY_WORK_WORKSHEETS_STATUS_FILTER_V2}
          reviewSummary={worksheetReviewSummary}
          reviewStatusLabels={WORKSHEET_REVIEW_ITEM_STATUS_LABELS}
          renderRows={({footerAction, hasCategoryWork, selectedIds}) => (
            <WorksheetsRows
              selectedWorksheetIds={selectedIds}
              hasCategoryWork={hasCategoryWork}
              footerAction={footerAction}
            />
          )}
        />
      )}
      {selectedCategory === MyWorkCategory.TEMPLATES && (
        <ReviewableMyWorkContent
          ariaLabel="Select template status"
          entityTypeLabel="templates"
          modelTypes={[ModelTypeConstants.TEMPLATE]}
          preferenceKey={HomepagePreferences.MY_WORK_TEMPLATES_STATUS_FILTER}
          reviewSummary={templateReviewSummary}
          showSearchFooter={false}
          renderRows={({footerAction, hasCategoryWork, selectedIds}) => (
            <TemplatesRows
              selectedTemplateIds={selectedIds}
              hasCategoryWork={hasCategoryWork}
              footerAction={footerAction}
            />
          )}
        />
      )}
      {selectedCategory === MyWorkCategory.TASKS && (
        <TasksMyWorkContent tasksReviewSummary={tasksReviewSummary} />
      )}
    </CardSection>
  );
}
